import { accountStorageKey } from './accountSessions';
import { legacyAuthHeader } from '../utils/legacyHostCompat';
/**
 * Worker Presence Service — profile management + AR location broadcasting
 */
import { hostWebApp } from './hostWebApp';
import { signedInBidUserId } from './bidBoardAuth';
// A worker's private card is personal data, not a protected company record, so it is
// persisted on-device and never gated behind a company account link. The server is a
// best-effort sync target on top of this local copy.
const WORKER_PROFILE_LOCAL_CARD_KEY = 'ziawellnav_worker_profile_card';
const LEGACY_AUTH_USER_ID_HEADER = legacyAuthHeader('User-Id');
const LEGACY_AUTH_USERNAME_HEADER = legacyAuthHeader('Username');
const LEGACY_AUTH_INIT_DATA_HEADER = legacyAuthHeader('Init-Data');


export const WORKER_TYPES = {
  // Production & Operations
  pumper: { label: 'Pumper', emoji: '⛽', color: '#22c55e' },
  gauger: { label: 'Gauger', emoji: '📊', color: '#10b981' },
  operator: { label: 'Lease Operator', emoji: '🏭', color: '#059669' },
  foreman: { label: 'Foreman', emoji: '📋', color: '#047857' },
  company_man: { label: 'Company Man', emoji: '👔', color: '#6366f1' },
  consultant: { label: 'Consultant', emoji: '🧑‍💼', color: '#818cf8' },
  // Drilling & Completion
  drilling: { label: 'Drilling', emoji: '⚙️', color: '#64748b' },
  derrickhand: { label: 'Derrickhand', emoji: '🪜', color: '#475569' },
  mudman: { label: 'Mud Engineer', emoji: '🧪', color: '#334155' },
  directional_driller: { label: 'Directional Driller', emoji: '🧭', color: '#1e293b' },
  frac: { label: 'Frac', emoji: '💧', color: '#0ea5e9' },
  coiled_tubing: { label: 'Coiled Tubing', emoji: '🌀', color: '#0284c7' },
  wireline: { label: 'Wireline', emoji: '🪢', color: '#14b8a6' },
  workover: { label: 'Workover', emoji: '🔩', color: '#0d9488' },
  cementer: { label: 'Cementer', emoji: '🧱', color: '#78716c' },
  snubbing: { label: 'Snubbing', emoji: '🔗', color: '#57534e' },
  flowback: { label: 'Flowback', emoji: '🌊', color: '#38bdf8' },
  // Heavy Equipment & Transport
  crane_operator: { label: 'Crane Operator', emoji: '🏗️', color: '#f59e0b' },
  truck_driver: { label: 'Truck Driver', emoji: '🚛', color: '#f97316' },
  hot_shot: { label: 'Hot Shot', emoji: '🏎️', color: '#ec4899' },
  vacuum_truck: { label: 'Vacuum Truck', emoji: '🚚', color: '#d97706' },
  water_hauler: { label: 'Water Hauler', emoji: '💦', color: '#06b6d4' },
  winch_truck: { label: 'Winch Truck', emoji: '🪝', color: '#b45309' },
  heavy_equipment: { label: 'Heavy Equipment', emoji: '🚜', color: '#92400e' },
  // Skilled Trades
  welder: { label: 'Welder', emoji: '🔥', color: '#ef4444' },
  electrician: { label: 'Electrician', emoji: '⚡', color: '#eab308' },
  pipeline: { label: 'Pipeline', emoji: '🔧', color: '#3b82f6' },
  roustabout: { label: 'Roustabout', emoji: '🛠️', color: '#78716c' },
  mechanic: { label: 'Mechanic', emoji: '🔧', color: '#a3a3a3' },
  millwright: { label: 'Millwright', emoji: '🏭', color: '#737373' },
  insulator: { label: 'Insulator', emoji: '🧤', color: '#d4d4d4' },
  sandblaster: { label: 'Sandblaster', emoji: '💨', color: '#fbbf24' },
  // Technology & Automation
  automation: { label: 'Automation / SCADA', emoji: '🤖', color: '#06b6d4' },
  telecom: { label: 'Telecom', emoji: '📡', color: '#8b5cf6' },
  it_support: { label: 'IT Support', emoji: '💻', color: '#7c3aed' },
  instrument_tech: { label: 'Instrument Tech', emoji: '🎛️', color: '#5b21b6' },
  // Safety & Compliance
  safety_hse: { label: 'Safety / HSE', emoji: '🛡️', color: '#a855f7' },
  inspector: { label: 'Inspector', emoji: '🔍', color: '#c084fc' },
  environmental: { label: 'Environmental', emoji: '🌿', color: '#4ade80' },
  medic: { label: 'Medic / EMT', emoji: '🏥', color: '#f43f5e' },
  // Support & Services
  caterer: { label: 'Caterer', emoji: '🍽️', color: '#fb923c' },
  water_transfer: { label: 'Water Transfer', emoji: '🚰', color: '#22d3ee' },
  chemical_tech: { label: 'Chemical Tech', emoji: '⚗️', color: '#a3e635' },
  surveyor: { label: 'Surveyor', emoji: '📐', color: '#facc15' },
  geologist: { label: 'Geologist', emoji: '🪨', color: '#a8a29e' },
  landman: { label: 'Landman', emoji: '📜', color: '#d6d3d1' },
  general: { label: 'General', emoji: '👷', color: '#94a3b8' },
} as const;

export type WorkerType = keyof typeof WORKER_TYPES;

export interface WorkerProfile {
  userId: string;
  username: string | null;
  displayName: string | null;
  workerType: WorkerType;
  company: string | null;
  businessAddress?: string | null;
  photoUrl: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  isPublic?: boolean;
  showPhone?: boolean;
  showWebsite?: boolean;
  showSignedName?: boolean;
  createdAt: string;
}

/** True when the user has actually saved shop-card fields (not just a Google sign-in name). */
export function hasFilledWorkerProfileCard(profile?: {
  workerType?: string | null;
  displayName?: string | null;
  company?: string | null;
  businessAddress?: string | null;
  phone?: string | null;
  website?: string | null;
  photoUrl?: string | null;
} | null): boolean {
  if (!profile) return false;
  const type = String(profile.workerType || '').trim();
  return Boolean(
    (type && type !== 'general')
    || String(profile.displayName || '').trim()
    || String(profile.company || '').trim()
    || String(profile.businessAddress || '').trim()
    || String(profile.phone || '').trim()
    || String(profile.website || '').trim()
    || String(profile.photoUrl || '').trim(),
  );
}

export function peekFilledLocalWorkerProfileCard(): boolean {
  try {
    const raw = localStorage.getItem(accountStorageKey(WORKER_PROFILE_LOCAL_CARD_KEY));
    if (raw && hasFilledWorkerProfileCard(JSON.parse(raw))) return true;
  } catch { /* ignore */ }
  try {
    return Boolean(localStorage.getItem('ziawellnav_worker_profile_business_address')?.trim());
  } catch {
    return false;
  }
}

export interface NearbyWorker {
  userId: string;
  username: string | null;
  displayName: string | null;
  workerType: WorkerType;
  photoUrl?: string | null;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  showPhone?: boolean;
  showWebsite?: boolean;
  showSignedName?: boolean;
  lat: number;
  lng: number;
  heading: number | null;
  speed: number | null;
  distance: number;
  lastUpdated: string;
}

class WorkerPresenceService {
  private profileRevision = 0;

  /** Synchronous, account-scoped startup fallback; never a credential. */
  getCachedProfile(): WorkerProfile | null {
    return this.readLocalProfileCard();
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const userId = hostWebApp.getUserId() || signedInBidUserId();
      const user = hostWebApp.getUser();
      const initData = hostWebApp.getInitData();
      if (userId) headers[LEGACY_AUTH_USER_ID_HEADER] = userId;
      if (user?.username) headers[LEGACY_AUTH_USERNAME_HEADER] = user.username;
      if (initData) headers[LEGACY_AUTH_INIT_DATA_HEADER] = initData;
    } catch { /* ignore */ }
    return headers;
  }

  private readLocalProfileCard(): WorkerProfile | null {
    try {
      const raw = localStorage.getItem(accountStorageKey(WORKER_PROFILE_LOCAL_CARD_KEY));
      if (!raw) return null;
      const card = JSON.parse(raw) as Partial<WorkerProfile> & { workerType?: WorkerType };
      return {
        userId: card.userId || 'local',
        username: card.username ?? null,
        displayName: card.displayName ?? null,
        workerType: (card.workerType as WorkerType) || 'general',
        company: card.company ?? null,
        businessAddress: card.businessAddress ?? null,
        photoUrl: card.photoUrl ?? null,
        phone: card.phone ?? null,
        email: card.email ?? null,
        website: card.website ?? null,
        isPublic: card.isPublic,
        showPhone: card.showPhone,
        showWebsite: card.showWebsite,
        showSignedName: card.showSignedName,
        createdAt: card.createdAt || new Date().toISOString(),
      };
    } catch { return null; }
  }

  private mergeLocalProfileCard(patch: Record<string, unknown>): void {
    this.profileRevision += 1;
    try {
      const existing = this.readLocalProfileCard() || {};
      const merged: Record<string, unknown> = { ...existing };
      for (const [key, value] of Object.entries(patch)) {
        if (value !== undefined) merged[key] = value;
      }
      localStorage.setItem(accountStorageKey(WORKER_PROFILE_LOCAL_CARD_KEY), JSON.stringify(merged));
    } catch { /* storage full/unavailable — non-fatal */ }
  }

  async getMyProfile(): Promise<WorkerProfile | null> {
    const requestAccount = accountStorageKey(WORKER_PROFILE_LOCAL_CARD_KEY);
    const requestRevision = this.profileRevision;
    try {
      const res = await fetch('/api/worker-profile', { headers: this.getHeaders() });
      if (res.ok) {
        const data = await res.json() as any;
        if (requestAccount !== accountStorageKey(WORKER_PROFILE_LOCAL_CARD_KEY)) return null;
        if (requestRevision !== this.profileRevision) return this.readLocalProfileCard();
        if (data?.profile) {
          this.mergeLocalProfileCard(data.profile);
          return data.profile;
        }
      }
    } catch { /* fall back to the on-device card below */ }
    return requestAccount === accountStorageKey(WORKER_PROFILE_LOCAL_CARD_KEY) ? this.readLocalProfileCard() : null;
  }

  async updateProfile(workerType: WorkerType, displayName?: string, company?: string, photoUrl?: string): Promise<boolean> {
    return this.updateProfileCard({ workerType, displayName, company, photoUrl });
  }

  async updateProfileCard(profile: {
    workerType?: WorkerType;
    displayName?: string | null;
    company?: string | null;
    businessAddress?: string | null;
    photoUrl?: string | null;
    phone?: string | null;
    email?: string | null;
    website?: string | null;
    isPublic?: boolean;
    showPhone?: boolean;
    showWebsite?: boolean;
    showSignedName?: boolean;
  }): Promise<boolean> {
    const privacyChange = ['isPublic', 'showPhone', 'showWebsite', 'showSignedName']
      .some(key => key in profile);
    if (privacyChange) {
      // A local-only success would falsely claim a public field was hidden.
      const requestAccount = accountStorageKey(WORKER_PROFILE_LOCAL_CARD_KEY);
      try {
        const response = await fetch('/api/worker-profile', {
          method: 'PUT', headers: this.getHeaders(), body: JSON.stringify(profile),
        });
        if (!response.ok || !(await response.json()).success
          || requestAccount !== accountStorageKey(WORKER_PROFILE_LOCAL_CARD_KEY)) return false;
        this.mergeLocalProfileCard(profile as Record<string, unknown>);
        return true;
      } catch { return false; }
    }
    // Persist the private card on-device first so it always saves — including for
    // native iOS installs that have no linked company account. Then sync to the server
    // best-effort. A personal card is never gated behind the company-account block.
    this.mergeLocalProfileCard(profile as Record<string, unknown>);
    try {
      await fetch('/api/worker-profile', {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(profile),
      });
    } catch { /* offline / unauthenticated — the local copy is the source of truth */ }
    return true;
  }

  /** Upload a photo file. Returns the new photo URL on success, null on failure.
   *  The endpoint also persists the URL into worker_profiles + worker_ar_presence. */
  async uploadPhoto(file: File): Promise<string | null> {
    const requestAccount = accountStorageKey(WORKER_PROFILE_LOCAL_CARD_KEY);
    try {
      const headers = this.getHeaders();
      // Don't set Content-Type — let the browser set the multipart boundary
      delete (headers as any)['Content-Type'];
      const formData = new FormData();
      formData.append('photo', file);
      const res = await fetch('/api/worker-photo', {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!res.ok) return null;
      const data = await res.json() as any;
      const photoUrl = String(data.photoUrl || '').trim() || null;
      if (requestAccount !== accountStorageKey(WORKER_PROFILE_LOCAL_CARD_KEY)) return null;
      if (photoUrl) this.mergeLocalProfileCard({ photoUrl });
      return photoUrl;
    } catch (e) {
      console.error('[workerPresenceService] uploadPhoto failed', e);
      return null;
    }
  }

  /** Clear the user's photo URL (does not delete the R2 object). */
  async deletePhoto(): Promise<boolean> {
    try {
      const res = await fetch('/api/worker-photo', {
        method: 'DELETE',
        headers: this.getHeaders(),
      });
      return res.ok;
    } catch { return false; }
  }

  async broadcastLocation(lat: number, lng: number, heading?: number, speed?: number): Promise<boolean> {
    try {
      const res = await fetch('/api/worker-presence', {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ latitude: lat, longitude: lng, heading, speed }),
      });
      return res.ok;
    } catch { return false; }
  }

  async getNearbyWorkers(lat: number, lng: number, radiusMiles = 5): Promise<NearbyWorker[]> {
    try {
      const res = await fetch(
        `/api/worker-presence?lat=${lat}&lng=${lng}&radius=${radiusMiles}`,
        { headers: this.getHeaders() }
      );
      if (!res.ok) return [];
      const data = await res.json() as any;
      return data.workers || [];
    } catch { return []; }
  }
}

export const workerPresenceService = new WorkerPresenceService();
export default workerPresenceService;
